import type { UtmParams } from '@/lib/utm-params';
import { formatPhoneNumber } from './formatPhoneNumber';

export interface RegisterPayloadInput {
  email: string;
  name: string;
  password: string;
  /**
   * Optional: sign-up no longer asks for the job title. When omitted, the backend
   * stores an empty value and the post-login ProfileCompletionModal collects it.
   */
  jobTitle?: string;
  phone: string;
  country: string;
  locale: 'en' | 'pt';
  emailMarketingOptIn: boolean;
  utmParams: UtmParams;
  /**
   * GA4 client id from the `_ga` cookie. Lets the server-side
   * `sign_up_completed` event keep this browser's channel attribution instead
   * of landing as a brand-new GA4 user. Undefined when GA4 is blocked — the
   * signup is still counted, just unattributed.
   */
  gaClientId?: string;
}

export interface RegisterPayload {
  username: string;
  name: string;
  email: string;
  password: string;
  job_title: string;
  phone_number: string;
  locale: 'en' | 'pt';
  email_marketing_opt_in: boolean;
  ga_client_id?: string;
  [utmKey: string]: unknown;
}

/**
 * Builds the body posted to `POST /api/auth/local/register`. Single source of
 * truth shared by `Register.tsx` and `LoginRegisterModal.tsx` so the two
 * sign-up entry points always send an identically-shaped payload.
 *
 * ToS/Privacy acceptance is intentionally NOT included here — the backend
 * stamps `tos_accepted`/`_version`/`_at` server-side on every registration.
 */
export function buildRegisterPayload(input: RegisterPayloadInput): RegisterPayload {
  const {
    email,
    name,
    password,
    jobTitle,
    phone,
    country,
    locale,
    emailMarketingOptIn,
    utmParams,
    gaClientId,
  } = input;

  return {
    username: email,
    name,
    email,
    password,
    job_title: jobTitle || '',
    phone_number: formatPhoneNumber(phone, country),
    locale,
    email_marketing_opt_in: emailMarketingOptIn,
    // Omitted rather than sent as undefined so the backend stores NULL, which
    // is what buildSignupEventPayload's fallback checks for.
    ...(gaClientId ? { ga_client_id: gaClientId } : {}),
    ...utmParams,
  };
}
